import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { useMe } from "@/hooks/useMe";
import type { UserPermissions } from "@/types";
import {
  Users,
  Briefcase,
  Landmark,
  ClipboardList,
  ShieldCheck,
  LogOut,
} from "lucide-react";

interface NavCard {
  title: string;
  description: string;
  icon: typeof Users;
  path: string;
  /** Permissão exigida (undefined = sempre visível para aprovados; "admin" = só admins) */
  requires?: keyof UserPermissions | "admin";
}

const cards: NavCard[] = [
  { title: "Investors", description: "Relationship Management", icon: Users, path: "/clients", requires: "can_view_clients" },
  { title: "Funds", description: "BLC I-V Portfolio", icon: Briefcase, path: "/funds", requires: "can_view_funds" },
  { title: "Assets", description: "Asset Portfolio", icon: Landmark, path: "/assets", requires: "can_view_assets" },
  { title: "Logs", description: "System Audit", icon: ClipboardList, path: "/audit-logs", requires: "admin" },
  { title: "Users", description: "Approval and Permissions", icon: ShieldCheck, path: "/users", requires: "admin" },
];

export default function Home() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const perms = me?.permissions;
  const mainCards = cards.filter((c) => {
    if (c.requires === "admin") return false;
    if (!c.requires) return true;
    return isAdmin || perms?.[c.requires];
  });
  const adminCards = isAdmin ? cards.filter((c) => c.requires === "admin") : [];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border" style={{ backgroundColor: "rgb(0, 56, 130)" }}>
        <div className="container mx-auto flex items-center justify-between py-5 px-4">
          <div className="flex items-center gap-3">
            <img src="/Logo JC Azul.png" alt="JusCapital" className="h-10 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm hidden sm:inline" style={{ color: "rgba(255,255,255,0.7)" }}>
              {user?.email}
            </span>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-white/10"
              style={{ color: "white" }}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold tracking-tight">
            Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h2>
          <p className="text-muted-foreground mt-2">
            Select a module below. Internal pages will be ported in future waves — for now this is the Azure-native skeleton running with Entra ID SSO.
          </p>
        </div>

        <div className="space-y-10 max-w-[80%]">
          {/* Main cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {mainCards.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.path}
                  onClick={() => navigate(c.path)}
                  className="group flex flex-col items-center text-center rounded-2xl border border-border bg-card px-6 py-8 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                  style={{ borderLeftWidth: "3px", borderLeftColor: "rgb(0, 56, 130)" }}
                >
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-2xl mb-4 transition-transform duration-200 group-hover:scale-110"
                    style={{ backgroundColor: "rgb(0, 56, 130)" }}
                  >
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-bold text-base mb-1">{c.title}</h3>
                  <p className="text-xs text-muted-foreground">{c.description}</p>
                </button>
              );
            })}
          </div>

          {/* Admin cards */}
          {adminCards.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Administration</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {adminCards.map((c) => {
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.path}
                      onClick={() => navigate(c.path)}
                      className="group flex flex-col items-center text-center rounded-xl border border-border bg-card px-4 py-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                      style={{ borderLeftWidth: "3px", borderLeftColor: "rgb(0, 56, 130)" }}
                    >
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-xl mb-3 transition-transform duration-200 group-hover:scale-110"
                        style={{ backgroundColor: "rgb(0, 56, 130)" }}
                      >
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-bold text-sm mb-1">{c.title}</h3>
                      <p className="text-xs text-muted-foreground">{c.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
